// @file Mobile app related state actions and mutations.
// * Note: we don't want to call postMessage directly here.
// * We want to instead set the state and let plugins/components respond and act
// * accordingly with native bridge.
import type { Reaction } from '@padlet/arvo'
import appCan from 'bits/app_can'
import { isAppUsing } from 'bits/flip'
import { circularSafeStringify } from 'bits/json_stringify'
import { anonymous, blankUser } from 'bits/user_model'
import type { SyncableSurfaceState } from 'native_bridge/actions'
import { postSurfaceState } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import type { LinkType } from 'native_bridge/types'
import { useReactionsStore } from 'pinia/surface_reactions'
import type { Cid, Id, Post, User, UserId } from 'types'
import type { Module } from 'vuex'
import type { RootState } from 'vuexstore/surface/types'

export interface NativeAppOpenLink {
  url: string
  linkType: LinkType
  target?: '_blank' | '_top' | '_self'
  contentCategory?: string
  contentSubcategory?: string
  username?: string
}

interface AppState {
  linkToOpen: NativeAppOpenLink | null
  // Reference: https://linear.app/padlet/issue/PRO-302/more-permanent-fix-for-drafts-native-component-covering-plus-button
  // the native component for draft posts may cover the bottom of the screen, preventing user from interacting with buttons at the bottom of shelf sections
  // => let the app decide how much padding is needed so the buttons are not covered by the native component
  shelfSectionBottomPaddingPx: number
}

interface ExpandedPostData {
  posts: Array<{
    id: Id
    cid: Cid
    has_attachment: boolean
  }>
  expanded_post: Post & {
    attachment_frame_url: string
    body_frame_url: string
    author_display_name: string
    author_avatar_url: string
    comments: Array<Comment & { user: User }>
  }
}

const NativeApp: Module<AppState, RootState> = {
  namespaced: true,
  state: {
    linkToOpen: null,
    shelfSectionBottomPaddingPx: 0,
  },
  getters: {
    expandedPostCommenterIds: (state, getters, rootState, rootGetters): Id[] => {
      const comments = rootGetters['comment/commentsByPost'][getters.expandedPost.id] || []
      return comments.map((c) => c.user_id)
    },
    expandedPost: (state, getters, rootState, rootGetters): Post | undefined => {
      return rootGetters['postAction/expandedPost']
    },
    expandedPostData: (state, getters, rootState, rootGetters): ExpandedPostData | {} => {
      // Versioning of the shape of `expanded_post_data` is tracked here https://stackoverflow.com/c/padlet/articles/1921
      const expandedPost = getters.expandedPost
      if (!expandedPost?.id || !appCan('nativeExpandedPostV1')) return {}

      const expandedPostPostsToNavigate: Post[] = rootGetters['postAction/expandedPostPostsToNavigate']
      const author = rootGetters['userContributor/getUserById'](expandedPost.author_id) || blankUser()
      const authorDisplayName = author.registered || !!author.registered_at ? author.name || author.username : anonymous
      const authorAvatarUrl = author.avatar
      let comments = rootGetters['comment/commentsByPost'][expandedPost.id] || []
      comments = comments.map((c) => ({
        ...c,
        user: rootGetters['userContributor/getUserById'](c.user_id),
      }))
      return {
        posts: expandedPostPostsToNavigate.map((p): object => ({
          id: p.id,
          cid: p.cid,
          has_attachment: !!p.attachment,
        })),
        expanded_post: {
          ...expandedPost,
          attachment_frame_url: rootGetters['postAction/expandedPostMobileFrameUrl'],
          body_frame_url: rootGetters['postAction/expandedPostMobileBodyFrameUrl'],
          author_display_name: authorDisplayName,
          author_avatar_url: authorAvatarUrl,
          comments,
        },
      }
    },
    shelfSectionBottomPaddingPx: (state): number => state.shelfSectionBottomPaddingPx,
  },
  mutations: {
    NATIVE_APP_OPEN_LINK(state, nativeAppOpenLink: NativeAppOpenLink): void {
      state.linkToOpen = nativeAppOpenLink
    },
    NATIVE_APP_LINK_OPENED(state): void {
      state.linkToOpen = null
    },
    NATIVE_APP_UPDATE_SHELF_SECTION_BOTTOM_PADDING(state, { new_padding }: { new_padding: number }): void {
      state.shelfSectionBottomPaddingPx = new_padding
    },
  },
  actions: {
    openLink({ commit }, nativeAppOpenLink: NativeAppOpenLink): void {
      commit('NATIVE_APP_OPEN_LINK', nativeAppOpenLink)
    },
    clearOpenLink({ commit }): void {
      commit('NATIVE_APP_LINK_OPENED')
    },
    hideAllPanelsAndMenus({ dispatch, rootState }): void {
      dispatch('settings/hideSettingsPanel', null, { root: true })
      dispatch('share/resetSharePanel', null, { root: true })
      dispatch('hideRemakePanel', null, { root: true })
      dispatch('hideConvertLayoutPanel', null, { root: true })
      dispatch('postAction/endPostCopy', null, { root: true })
      dispatch('postAction/endPostTransfer', null, { root: true })
      if (rootState.postAction && rootState.postAction.postUnderActionCid) {
        dispatch('postAction/hidePostActionMenu', null, { root: true })
      }
      if (rootState.section && rootState.section.actionMenuSectionId) {
        dispatch('section/hideSectionActionMenu', null, { root: true })
      }
      if (rootState.comment && rootState.comment.actionMenuCommentId) {
        dispatch('comment/hideCommentActionMenu', null, { root: true })
      }
    },
    updateShelfSectionBottomPadding({ commit }, payload: { new_padding: number }): void {
      commit('NATIVE_APP_UPDATE_SHELF_SECTION_BOTTOM_PADDING', payload)
    },
    // data that will be sent only once should be put in transientPayload
    async postSurfaceState(
      { getters, rootGetters, dispatch, rootState },
      transientPayload?: { collaborator_just_left_user_id?: UserId },
    ): Promise<void> {
      if (!rootGetters.isApp) {
        return
      }
      if (getters.expandedPost?.author_id) {
        await dispatch('userContributor/fetchUser', getters.expandedPost?.author_id, { root: true })
        await Promise.all(
          getters.expandedPostCommenterIds.map(
            async (id): Promise<void> => await dispatch('userContributor/fetchUser', id, { root: true }),
          ),
        )
      }

      const { accumulatedReactionsByWishId } = useReactionsStore()
      const reactions = Object.values(accumulatedReactionsByWishId).reduce<Reaction[]>((acc, val) => {
        if (val?.userReaction != null) {
          acc.push(val.userReaction)
        }
        return acc
      }, [])
      const plainObjectAppState: SyncableSurfaceState = JSON.parse(
        circularSafeStringify({
          wall_sections: rootGetters['section/sortedSections'],
          oauth_token: rootGetters.oauthToken,
          reactions: isAppUsing('accumulatedReactions') ? reactions : rootGetters['reaction/reactions'],
          wall_reaction_data: rootGetters.reactionData,
          expanded_post_data: getters.expandedPostData,
          is_remake_toggleable: true,
          // amICollaborator is to show/hide the "Leave padlet" button in the mobile app
          am_i_collaborator: rootGetters.amICollaborator,
          collaborator_just_left_user_id: transientPayload?.collaborator_just_left_user_id,
          // Permissions
          can_i_write: rootGetters.canIWrite,
          can_i_edit: rootGetters.canIEdit,
          can_i_administer: rootGetters.canIAdminister,
          // Surface V2 is launched in Jan 2023 Winter release. This flag should be true for all users after public release.
          is_surface_v2_launched: appCan('useSurfaceV2') || isAppUsing('surfaceSections'),
          // App needs knowledge of cid to id mapping that we have assigned to drafts
          post_drafts_by_cid: isAppUsing('postDrafts') ? rootState.draft.draftByCid : undefined,
        }),
      )
      postMessage(
        postSurfaceState({
          ...plainObjectAppState,
        }),
      )
    },
  },
}

export default NativeApp
